import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth';
import { getCapabilities, type CapabilityDocument, type FeatureState } from './api/me.gen';
import { applyBrandingOverlay, cacheBrandingOverlay, EMPTY_BRANDING_OVERLAY } from './branding';

// CapabilityProvider (P01 §4 / P02 §3, story 1C-1): boot-fetches /me/capabilities
// (the single contract from 1B-2) and refetches when the governance SSE channel
// says the doc changed. The whole UI shell gates off this — nav entries,
// FlagGate, PermissionGate all read from here.

const EMPTY: CapabilityDocument = {
  permissions: [],
  features: {},
  permVersion: 0,
  flagVersion: 0,
  platformRole: null,
  orgKind: null,
  orgName: null,
  roleNames: [],
  branding: EMPTY_BRANDING_OVERLAY,
};
// Unknown feature keys default to 'on' so a surface without a seeded feature is
// never accidentally hidden (features are flagged in as they land — 1F-2).
const DEFAULT_FEATURE: FeatureState = { state: 'on' };

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

interface CapabilitiesContextValue {
  doc: CapabilityDocument;
  isHydrated: boolean;
  hasPermission: (key: string) => boolean;
  featureState: (key: string) => FeatureState;
  refetch: () => Promise<void>;
}

const CapabilitiesContext = createContext<CapabilitiesContextValue | undefined>(undefined);

export function CapabilityProvider({ children }: { children: ReactNode }): JSX.Element {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [doc, setDoc] = useState<CapabilityDocument>(EMPTY);
  const [isHydrated, setIsHydrated] = useState(false);

  const refetch = useCallback(async () => {
    if (!token) return;
    try {
      const next = await getCapabilities();
      setDoc(next);
      setIsHydrated(true);
    } catch {
      // Keep the last-known doc on a transient failure; a later SSE/refetch recovers.
    }
  }, [token]);

  // Boot fetch (and clear on logout). Logout also clears the branding overlay
  // (applied + cached) — otherwise `main.tsx`'s next-boot cache-apply would
  // paint the JUST-LOGGED-OUT org's brand color for whoever logs in next on
  // this browser, before their own fetch resolves.
  useEffect(() => {
    if (!token) {
      setDoc(EMPTY);
      setIsHydrated(false);
      applyBrandingOverlay(EMPTY_BRANDING_OVERLAY);
      cacheBrandingOverlay(EMPTY_BRANDING_OVERLAY);
      return;
    }
    void refetch();
  }, [token, refetch]);

  // Story 6-8, P10 §3: "inject Layer-2 CSS vars" on every resolved doc — the
  // initial boot fetch AND every later SSE-triggered refetch. A branding save
  // (`orgBranding.service#setBranding`/`uploadLogo`) calls the SAME
  // `bumpFlagVersion` every other org-config change already calls, which
  // pushes `capabilities.changed` on the existing governance SSE channel — so
  // THIS tab's mutation response, and every OTHER open tab/device's live
  // refetch, both land here and get the "soft re-theme without reload" P10
  // §3 asks for, with no new plumbing. Re-caches on every apply so the NEXT
  // page load's synchronous pre-paint apply (main.tsx) stays current.
  useEffect(() => {
    applyBrandingOverlay(doc.branding);
    cacheBrandingOverlay(doc.branding);
  }, [doc.branding]);

  // Governance SSE channel → refetch on `capabilities.changed`. fetch-stream (not
  // EventSource) so we can send the Authorization header; reconnects with backoff.
  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    let stopped = false;

    const readStream = async (): Promise<void> => {
      const response = await fetch(`${baseURL}/events/governance`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error(`governance SSE ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done) return;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        frames.forEach((frame) => {
          const dataLine = frame.split('\n').find((line) => line.startsWith('data:'));
          if (!dataLine) return;
          try {
            const event = JSON.parse(dataLine.slice(5).trim());
            if (event && event.type === 'capabilities.changed') {
              void refetch();
              // Story 2-73 (A2/A3, P18 §4): a permission/flag/org-config
              // change delivered live must reach every already-cached
              // protected list/detail, not just THIS provider's own doc —
              // before this, `capabilities.changed` updated `doc` (nav,
              // FlagGate, PermissionGate) but left react-query's cache alone,
              // so a role revoked mid-session (or a feature re-locked) still
              // rendered stale allowed/visible rows until the next manual
              // navigation or hard refresh. Same blanket `invalidateQueries()`
              // (marks everything stale, refetches active queries in the
              // background) `handleAuthSuccess()` already uses on login/
              // signup/oauth (`lib/auth.tsx`) — not the harder `.clear()`
              // logout uses, since this event fires for in-session changes
              // the user is actively looking at and a full cache wipe would
              // flash every open surface to a loading state for what is
              // usually a single flag/role edit.
              void queryClient.invalidateQueries();
            }
          } catch {
            // ignore keep-alive / non-JSON frames
          }
        });
      }
    };

    const run = async (): Promise<void> => {
      let backoff = 1000;
      while (!stopped) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await readStream();
          backoff = 1000;
        } catch {
          if (stopped) return;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, backoff));
        backoff = Math.min(backoff * 2, 30000);
      }
    };

    void run();
    return () => {
      stopped = true;
      controller.abort();
    };
  }, [token, refetch, queryClient]);

  const value = useMemo<CapabilitiesContextValue>(() => {
    const permissionSet = new Set(doc.permissions);
    return {
      doc,
      isHydrated,
      hasPermission: (key: string) => permissionSet.has(key),
      featureState: (key: string) => doc.features[key] ?? DEFAULT_FEATURE,
      refetch,
    };
  }, [doc, isHydrated, refetch]);

  return <CapabilitiesContext.Provider value={value}>{children}</CapabilitiesContext.Provider>;
}

export function useCapabilities(): CapabilitiesContextValue {
  const context = useContext(CapabilitiesContext);
  if (!context) {
    throw new Error('useCapabilities must be used within CapabilityProvider');
  }
  return context;
}

export interface CapabilityGate {
  /** Render the control as usable. Both halves said yes. */
  allowed: boolean;
  /** The feature is entitled-but-off / not entitled — render the teaser, not a 403. */
  locked: boolean;
  /** The capability document has not arrived yet — render neither state. */
  isHydrated: boolean;
  /**
   * Why the control is not usable, for the copy beside a disabled control.
   * `null` when it IS usable. Deliberately a REASON, not a sentence: the
   * caller owns the wording (and a server refusal owns its own, G-27).
   */
  reason: 'loading' | 'flag' | 'permission' | null;
}

/**
 * The single gate every CTA reads (DOD §4 G-13: "No route or CTA renders
 * 'New X' without `useCapabilityGate(featureKey, permission)`").
 *
 * The gate the rule names did not exist in the tree — every surface reached for
 * `useCapabilities()` and combined `hasPermission` with `featureState` itself,
 * which is exactly the per-screen decision-making G-13 was written to end. It
 * is one hook, not a component, because the answer is needed for `disabled`,
 * for the explanatory copy next to a disabled control, and for whether to
 * render a `PlanTeaser` at all — three consumers of one decision.
 *
 * It reports THREE outcomes, not two, because "you may not" and "your plan does
 * not include this" are different products: `locked` is an upsell with copy the
 * discovery record already owns, `permission` is an access answer. Collapsing
 * them is how a paying customer gets told they lack permission for something
 * they simply have not bought.
 *
 * Both halves are advisory: the capability document ADVERTISES and the backend
 * ENFORCES (DOD §1.11). A control this hook allows can still be refused by the
 * API, and that refusal — with the server's own sentence — is what the user
 * sees. Nothing here is a substitute for the gate on the route.
 */
export function useCapabilityGate(featureKey: string | undefined, permission?: string): CapabilityGate {
  const { hasPermission, featureState, isHydrated } = useCapabilities();
  if (!isHydrated) return { allowed: false, locked: false, isHydrated: false, reason: 'loading' };
  const state = featureKey ? featureState(featureKey).state : 'on';
  if (state !== 'on') {
    return { allowed: false, locked: state === 'locked', isHydrated: true, reason: 'flag' };
  }
  if (permission && !hasPermission(permission)) {
    return { allowed: false, locked: false, isHydrated: true, reason: 'permission' };
  }
  return { allowed: true, locked: false, isHydrated: true, reason: null };
}
